import React from "react";
import { Link } from "react-router-dom";

const PRINCIPLES = [
  {
    title: "Wallet-controlled execution",
    description:
      "Krionex never asks for private keys or seed phrases. Deployments and token operations are confirmed through the user’s connected wallet.",
  },
  {
    title: "Explorer-backed transparency",
    description:
      "Deployment records include contract addresses, transaction hashes, verification state, and links to public block explorers where supported.",
  },
  {
    title: "Standardized contract patterns",
    description:
      "Krionex supports ERC20, ERC721, and ERC1155 deployment workflows using modular, repeatable contract configurations.",
  },
];

const SECURITY_ITEMS = [
  {
    title: "Non-custodial by design",
    body:
      "Krionex does not custody user funds, wallet assets, private keys, or seed phrases. Network gas and contract interactions are handled through the user’s wallet.",
  },
  {
    title: "Wallet confirmation required",
    body:
      "Deployments, minting, burning, pause, and unpause actions require wallet confirmation before they are broadcast to the selected blockchain network.",
  },
  {
    title: "Deployment records",
    body:
      "Users can review deployment history, transaction references, contract addresses, and verification status from their workspace.",
  },
  {
    title: "Preflight checks",
    body:
      "Where supported, Krionex runs preflight checks to validate deployment configuration before users spend real network gas.",
  },
  {
    title: "Billing separation",
    body:
      "Krionex subscriptions cover platform access. Blockchain gas fees, wallet fees, and network costs are paid separately by the user.",
  },
  {
    title: "Support and account help",
    body:
      "Users can access Help Center, contact support, and review legal policies directly from the Krionex workspace.",
  },
];

export default function Trust() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="relative p-6 sm:p-8 lg:p-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(197,155,69,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(15,23,42,0.06),transparent_32%)]" />

            <div className="relative max-w-4xl">
              <div className="inline-flex rounded-full border border-[#C59B45]/30 bg-[#FFF8E1] px-3 py-1 text-xs font-semibold text-[#7A5A12]">
                Trust Center
              </div>

              <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
                Security, transparency, and control for on-chain deployments.
              </h1>

              <p className="mt-5 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base sm:leading-8">
                Krionex is designed as a non-custodial smart contract deployment
                and token operations workspace. Users configure contracts, review
                deployment details, confirm actions through their own wallet, and
                track activity through public explorer-backed records.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/examples"
                  className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  View verified examples
                </Link>

                <Link
                  to="/help-center"
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-50"
                >
                  Open Help Center
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-3">
          {PRINCIPLES.map((item) => (
            <div
              key={item.title}
              className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#C59B45]/25 bg-[#FFF8E1] text-sm font-bold text-[#7A5A12]">
                ✓
              </div>

              <h2 className="mt-5 text-lg font-semibold tracking-tight text-slate-950">
                {item.title}
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-600">
                {item.description}
              </p>
            </div>
          ))}
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
              Platform safeguards
            </div>

            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
              Built for clear ownership and verifiable activity.
            </h2>

            <p className="mt-3 text-sm leading-7 text-slate-600">
              Krionex keeps critical actions wallet-confirmed and makes deployment
              activity easy to inspect through dashboards, transaction records,
              and explorer references.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {SECURITY_ITEMS.map((item) => (
              <div
                key={item.title}
                className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-5"
              >
                <h3 className="text-sm font-semibold text-slate-950">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm leading-7 text-slate-600">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
              User responsibility
            </div>

            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
              Blockchain actions are final once confirmed on-chain.
            </h2>

            <p className="mt-3 text-sm leading-7 text-slate-600">
              Krionex helps users prepare and submit deployment and token
              operations, but wallet-confirmed blockchain transactions are executed
              on public networks. Users should review wallet prompts, contract
              configuration, network selection, gas fees, and recipient addresses
              before confirming any transaction.
            </p>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-[#050713] p-6 text-white shadow-sm sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
              Policies
            </div>

            <h2 className="mt-3 text-2xl font-semibold tracking-tight">
              Legal and support resources.
            </h2>

            <div className="mt-5 grid gap-3 text-sm">
              <Link
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 font-semibold text-white transition hover:bg-white/[0.1]"
                to="/terms"
              >
                Terms of Service
              </Link>

              <Link
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 font-semibold text-white transition hover:bg-white/[0.1]"
                to="/privacy"
              >
                Privacy Policy
              </Link>

              <Link
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 font-semibold text-white transition hover:bg-white/[0.1]"
                to="/refund-policy"
              >
                Refund Policy
              </Link>

              <Link
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 font-semibold text-white transition hover:bg-white/[0.1]"
                to="/contact"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}